import { Loader2 } from 'lucide-react';
import { useGetEtlPipelineStatusQuery } from 'src/redux/operations/operations.endpoints';
import { formatDisplayDateTime } from 'src/helpers/date.helpers';
import { EtlStatusBadge } from './etl-status-badge';

function formatLastRun(value: string | null | undefined): string {
  if (!value) {
    return '—';
  }
  const formatted = formatDisplayDateTime(value);
  return formatted || value;
}

export function PipelineStatusSection() {
  const { data, isLoading, isError, error, isFetching, refetch } = useGetEtlPipelineStatusQuery();

  return (
    <section
      className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5"
      aria-labelledby="etl-pipeline-status-heading"
    >
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id="etl-pipeline-status-heading" className="text-base font-semibold text-gray-900">
            Pipeline status
          </h2>
          <p className="text-sm text-gray-600">Current state of the ETL pipeline from the external API.</p>
        </div>
        <button
          type="button"
          className="text-sm font-medium text-violet-700 hover:text-violet-900 disabled:opacity-50"
          onClick={() => void refetch()}
          disabled={isFetching}
        >
          Refresh
        </button>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-gray-600" role="status">
          <Loader2 className="size-4 animate-spin" />
          Loading pipeline status…
        </div>
      )}

      {isError && !isLoading && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800" role="alert">
          <p className="font-medium">Could not load pipeline status</p>
          <p className="mt-1 text-red-700">
            {(error as { message?: string })?.message ?? 'An unexpected error occurred.'}
          </p>
        </div>
      )}

      {!isLoading && !isError && !data?.status && !data?.last_run_at && (
        <p className="text-sm text-gray-600">No pipeline status is available right now.</p>
      )}

      {!isLoading && !isError && (data?.status || data?.last_run_at) && (
        <div className="grid gap-4 sm:grid-cols-2">
          <EtlStatusBadge label="Status" value={data?.status ?? null} />
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-600">Last run</span>
            <span className="text-sm font-semibold text-gray-900">{formatLastRun(data?.last_run_at)}</span>
          </div>
          {data?.message ? (
            <p className="sm:col-span-2 text-sm text-gray-600">{data.message}</p>
          ) : null}
        </div>
      )}
    </section>
  );
}
